/**
 * AppLayout — GroupedSidebar (narrow-default + hover-expand + pin) + main content.
 *
 * Authority: docs/superpowers/plans/2026-04-21-inbox-cockpit-and-sidebar-hover.md §4.2
 * Sidebar:   GroupedSidebar renders fixed at left. When unpinned, main content
 *            keeps the collapsed-width gutter and the rail overlays on hover.
 *            When pinned, the gutter widens to the expanded width so content
 *            never sits under the rail.
 * Motion:    DS_MOTION.navigation.crossfadeDuration (180ms) via Framer Motion
 *            AnimatePresence on route change.
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { GroupedSidebar } from './GroupedSidebar';
import { LayoutModeProvider, useLayoutMode } from './LayoutModeContext';
import { SIDEBAR_SECTIONS, SIDEBAR_SETTINGS, API_PATHS } from '../../config/product-contract';
import { getLinkedInDraftCount, getCanonicalInbox } from '../../lib/api';
import { UI_INVARIANTS, DS_MOTION, DS_SPACING } from '../../config/ui-invariants';
import { useSidebarPinned } from '../../hooks/useSidebarPinned';
import { Toaster } from 'sonner';

export default function AppLayout({ children }) {
  return (
    <LayoutModeProvider>
      <AppLayoutInner>{children}</AppLayoutInner>
    </LayoutModeProvider>
  );
}

function AppLayoutInner({ children }) {
  const location = useLocation();
  const [badgeCounts, setBadgeCounts] = useState({});
  const { isPinned, togglePin } = useSidebarPinned();
  const { fullBleed } = useLayoutMode();

  useEffect(() => {
    window.__ACCREZEO_LAYOUT_MOUNTED__ = true;
    if (!UI_INVARIANTS.SESSION.getIsLifecycleComplete()) {
      console.error('[CRITICAL_VIOLATION] AppLayout mounted BEFORE Lifecycle Completion.');
      throw new Error('SEQ_LIFECYCLE_VIOLATION: Main app mounted during welcome sequence.');
    }
  }, []);

  useEffect(() => {
    const fetchBadgeCounts = async () => {
      const counts = {};
      for (const section of SIDEBAR_SECTIONS) {
        for (const item of section.items || []) {
          if (item.badge && item.badge.apiPath) {
            try {
              let count = 0;
              if (item.badge.apiPath === API_PATHS.LINKEDIN_DRAFT_COUNT) {
                const res = await getLinkedInDraftCount();
                count = res?.count ?? 0;
              } else if (item.badge.apiPath === API_PATHS.WORK_INBOX) {
                const res = await getCanonicalInbox();
                count = res?.items?.length ?? 0;
              }
              if (count > 0) counts[item.id] = count;
            } catch {
              // Silent failure — missing badge is fine, broken sidebar is not
            }
          }
        }
      }
      setBadgeCounts(counts);
    };
    fetchBadgeCounts();
  }, []);

  const mainPaddingLeft = isPinned
    ? DS_SPACING.sidebarWidthExpanded
    : DS_SPACING.sidebarWidthCollapsed;

  return (
    <div className="h-screen font-sans" style={{ position: 'relative' }}>
      <GroupedSidebar
        sections={SIDEBAR_SECTIONS}
        settingsItem={SIDEBAR_SETTINGS.item}
        badgeCounts={badgeCounts}
        isPinned={isPinned}
        onTogglePin={togglePin}
      />

      <main
        className="h-screen overflow-y-auto bg-canvas-base focus:outline-none scrollbar-hide"
        tabIndex="-1"
        style={{
          position: 'relative',
          paddingLeft: `${mainPaddingLeft}px`,
          transition: `padding-left ${DS_MOTION.sidebar.widthDurationMs}ms ${DS_MOTION.sidebar.widthEase}`,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: DS_MOTION.navigation.crossfadeDuration / 1000,
              ease: DS_MOTION.navigation.crossfadeEase,
            }}
            style={{ minHeight: '100%' }}
          >
            <div
              data-layout-mode={fullBleed ? 'fullBleed' : 'centered'}
              className={fullBleed
                ? 'w-full min-h-screen'
                : 'max-w-[840px] mx-auto py-8 px-10 min-h-screen'}
            >
              {children}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="fixed bottom-4 right-4 text-xs text-slate-400 font-mono">
          v ({process.env.REACT_APP_GIT_SHA?.substring(0, 7) || 'dev'})
        </div>
      </main>

      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
